import { CopyButton } from '@/components/common/CopyButton'
import { DomainStatusBadge } from './DomainStatusBadge'
import type { DnsRecordDto } from '@/types/sendingDomains'

interface DnsRecordsTableProps {
  records: DnsRecordDto[]
}

/**
 * The DNS records the customer must publish, each copyable, with per-record status.
 * Stacks into cards on mobile (so the long DKIM values never get clipped) and renders as a
 * fixed-layout table on sm+ (wide Name/Host + a dedicated Priority column).
 */
export function DnsRecordsTable({ records }: DnsRecordsTableProps) {
  if (!records || records.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">No DNS records yet — they appear once the domain is created.</p>
  }

  return (
    <>
      {/* Mobile: stacked cards */}
      <div className="space-y-3 sm:hidden">
        {records.map((record, i) => (
          <div key={`m-${record.Type}-${record.Name}-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                {record.Type}
                {record.RecordType && <span className="ml-1 text-slate-400">({record.RecordType})</span>}
              </span>
              <DomainStatusBadge status={record.Status} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Name / Host</div>
              <div className="flex items-start gap-1.5">
                <code className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">{record.Name}</code>
                <CopyButton text={record.Name} label="Host" variant="icon-only" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Value</div>
              <div className="flex items-start gap-1.5">
                <code className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">{record.Value}</code>
                <CopyButton text={record.Value} label="Value" variant="icon-only" />
              </div>
            </div>
            {record.Priority != null && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Priority</div>
                <span className="inline-block rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs font-semibold text-slate-700 dark:text-slate-200">{record.Priority}</span>
              </div>
            )}
            <div className="text-[10px] text-slate-400">TTL {record.Ttl || 'Auto'}</div>
          </div>
        ))}
      </div>

      {/* sm+: fixed-layout table */}
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full table-fixed text-left text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
            <tr>
              <th className="w-[11%] px-3 py-2 font-medium">Type</th>
              <th className="w-[27%] px-3 py-2 font-medium">Name / Host</th>
              <th className="w-[36%] px-3 py-2 font-medium">Value</th>
              <th className="w-[9%] px-3 py-2 font-medium">Priority</th>
              <th className="w-[8%] px-3 py-2 font-medium">TTL</th>
              <th className="w-[9%] px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {records.map((record, i) => (
              <tr key={`${record.Type}-${record.Name}-${i}`} className="align-top">
                <td className="px-3 py-2">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{record.Type}</span>
                  {record.RecordType && <span className="ml-1 text-slate-400">({record.RecordType})</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-start gap-1.5">
                    <code className="font-mono text-slate-700 dark:text-slate-200 break-all">{record.Name}</code>
                    <CopyButton text={record.Name} label="Host" variant="icon-only" />
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-start gap-1.5">
                    <code className="font-mono text-slate-700 dark:text-slate-200 break-all">{record.Value}</code>
                    <CopyButton text={record.Value} label="Value" variant="icon-only" />
                  </div>
                </td>
                <td className="px-3 py-2">
                  {record.Priority != null
                    ? <span className="inline-block rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-semibold text-slate-700 dark:text-slate-200">{record.Priority}</span>
                    : <span className="text-slate-300 dark:text-slate-600">—</span>}
                </td>
                <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{record.Ttl || 'Auto'}</td>
                <td className="px-3 py-2">
                  <DomainStatusBadge status={record.Status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
